<!-- =========================================================================================
  File Name: VerticalNavMenu.vue
  Description: Vertical NavMenu Component
  Component Name: VerticalNavMenu
  ----------------------------------------------------------------------------------------
  Item Name: Virtual Job Experience
    Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <vs-sidebar
    class="v-nav-menu items-no-padding"
    v-model="isVerticalNavMenuActive"
    ref="verticalNavMenu"
    default-index="-1"
    :click-not-close="clickNotClose"
    :reduce-not-rebound="reduceNotRebound"
    :parent="parent"
    :hiddenBackground="clickNotClose"
    :reduce="reduce"
    v-hammer:swipe="onMenuSwipe"
  >
    <div @mouseenter="mouseEnter" @mouseleave="mouseLeave">
      <!-- Header -->
      <div class="header-sidebar flex items-end justify-center relative" slot="header">
        <!-- Logo -->
        <router-link
          tag="div"
          class="vx-logo cursor-pointer flex items-center"
          :to="`/${login_type}/dashboard`"
        >
          <img
            src="@/assets/images/logo/logo.png"
            alt="Home - Virtual Job Experience logo"
            id="logo1"
            v-show="isMouseEnter || !reduce"
          />
          <img
            src="@/assets/images/logo/logo1.png"
            alt="Home - Virtual Job Experience logo"
            v-show="!isMouseEnter && reduce"
          />
        </router-link>
        <!-- /Logo -->
      </div>
      <!-- /Header -->

      <!-- Header Shadow -->
      <div class="shadow-bottom" v-show="showShadowBottom" />

      <!-- Menu Items -->

      <div class="px-2 menu-items">
        <!-- Sections. Each group header is a real heading (audit issue 11) and each set of
             links is a real list (audit issue 12); the nav was previously a flat run of
             <span>s and <div>s, so neither was exposed to assistive tech. -->
        <template v-for="(section, sectionIndex) in menuSections">
          <h3
            v-if="section.header && section.header.trim() && !verticalNavMenuItemsMin"
            :key="`header-${sectionIndex}`"
            class="navigation-header truncate"
          >
            {{ section.header }}
          </h3>

          <ul :key="`list-${sectionIndex}`" class="vs-sidebar--list">
            <template v-for="(item, index) in section.items">
              <!-- Nav-Item -->
              <v-nav-menu-item
                v-if="!item.submenu"
                :key="`item-${sectionIndex}-${index}`"
                :index="index"
                :to="item.slug !== 'external' ? item.url : null"
                :href="item.slug === 'external' ? item.url : null"
                :icon="item.icon"
                :iconType="item.iconType"
                :target="item.target"
                :isDisabled="item.isDisabled"
                :slug="item.slug"
                :isHelpPopupBtn="
                  typeof item.isHelpPopupBtn !== 'undefined' ? item.isHelpPopupBtn : false
                "
              >
                <span v-show="!verticalNavMenuItemsMin" class="">{{ item.name }}</span>
                <vs-chip
                  class="ml-auto"
                  :color="item.tagColor"
                  v-if="item.tag && (isMouseEnter || !reduce)"
                  >{{ item.tag }}
                </vs-chip>
              </v-nav-menu-item>
              <!-- /Nav-Item -->

              <!-- Nav-Group -->
              <v-nav-menu-group
                v-else
                :key="`group-${sectionIndex}-${index}`"
                :openHover="openGroupHover"
                :group="item"
                :groupIndex="index"
                :open="isGroupActive(item)"
                :slug="item.slug"
              />
              <!-- /Nav-Group -->
            </template>
          </ul>
        </template>
        <!-- /Menu Items -->
      </div>
    </div>
  </vs-sidebar>
</template>

<script>
import VNavMenuGroup from './VerticalNavMenuGroup.vue';
import VNavMenuItem from './VerticalNavMenuItem.vue';

import Logo from '../Logo.vue';

export default {
  name: 'v-nav-menu',
  components: {
    VNavMenuGroup,
    VNavMenuItem,
    Logo,
  },
  props: {
    logo: { type: String },
    openGroupHover: { type: Boolean, default: false },
    parent: { type: String },
    reduceNotRebound: { type: Boolean, default: true },
    navMenuItems: { type: Array, required: true },
    title: { type: String },
  },
  data: () => ({
    logoutIcon: 'LogOutIcon',
    clickNotClose: false, // disable close navMenu on outside click
    isMouseEnter: false,
    reduce: false, // determines if navMenu is reduce - component property
    showCloseButton: false, // show close button in smaller devices
    settings: {
      // perfectScrollbar settings
      maxScrollbarLength: 60,
      wheelSpeed: 1,
      swipeEasing: true,
    },
    showShadowBottom: false,
    login_type: localStorage.getItem('login_type'),
  }),
  computed: {
    isGroupActive() {
      return (item) => {
        const path = this.$route.fullPath;
        const routeParent = this.$route.meta ? this.$route.meta.parent : undefined;
        let open = false;

        const func = (item) => {
          if (item.submenu) {
            item.submenu.forEach((item) => {
              if (item.url && (path === item.url || routeParent === item.slug)) {
                open = true;
              } else if (item.submenu) {
                func(item);
              }
            });
          }
        };
        func(item);
        return open;
      };
    },
    // The nav data is already grouped as [{header, items}]; menuItemsUpdated flattens it
    // for other consumers. This keeps the grouping so each section can render a heading
    // plus its own list (audit issues 11 and 12).
    menuSections() {
      const sections = [];
      let loose = { header: null, items: [] };

      this.navMenuItems.forEach((entry) => {
        if (entry.header) {
          if (loose.items.length) sections.push(loose);
          sections.push({ header: entry.header, items: (entry.items || []).slice() });
          loose = { header: null, items: [] };
        } else {
          loose.items.push(entry);
        }
      });

      if (loose.items.length) sections.push(loose);
      return sections;
    },
    menuItemsUpdated() {
      const clone = this.navMenuItems.slice();

      for (const [index, item] of this.navMenuItems.entries()) {
        if (item.header && item.items.length && (index || 1)) {
          const i = clone.findIndex((ix) => ix.header === item.header);
          for (const [subIndex, subItem] of item.items.entries()) {
            clone.splice(i + 1 + subIndex, 0, subItem);
          }
        }
      }

      return clone;
    },
    isVerticalNavMenuActive: {
      get() {
        return this.$store.state.isVerticalNavMenuActive;
      },
      set(val) {
        this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', val);
      },
    },
    layoutType() {
      return this.$store.state.mainLayoutType;
    },
    reduceButton: {
      get() {
        return this.$store.state.reduceButton;
      },
      set(val) {
        this.$store.commit('TOGGLE_REDUCE_BUTTON', val);
      },
    },
    isVerticalNavMenuReduced() {
      return Boolean(this.reduce && this.reduceButton);
    },
    verticalNavMenuItemsMin() {
      return this.$store.state.verticalNavMenuItemsMin;
    },
    scrollbarTag() {
      return this.$store.getters.scrollbarTag;
    },
    windowWidth() {
      return this.$store.state.windowWidth;
    },
  },
  watch: {
    $route() {
      if (this.isVerticalNavMenuActive && this.showCloseButton)
        this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', false);
    },
    reduce(val) {
      const verticalNavMenuWidth = val
        ? 'reduced'
        : this.$store.state.windowWidth < 1200
        ? 'no-nav-menu'
        : 'default';
      this.$store.dispatch('updateVerticalNavMenuWidth', verticalNavMenuWidth);

      setTimeout(function () {
        window.dispatchEvent(new Event('resize'));
      }, 100);
    },
    layoutType() {
      this.setVerticalNavMenuWidth();
    },
    reduceButton() {
      this.setVerticalNavMenuWidth();
    },
    windowWidth() {
      this.setVerticalNavMenuWidth();
    },
  },
  methods: {
    onMenuSwipe(event) {
      if (event.direction === 4 && this.$vs.rtl) {
        // Swipe Right
        if (this.isVerticalNavMenuActive && this.showCloseButton)
          this.isVerticalNavMenuActive = false;
      } else if (event.direction === 2 && !this.$vs.rtl) {
        // Swipe Left
        if (this.isVerticalNavMenuActive && this.showCloseButton)
          this.isVerticalNavMenuActive = false;
      }
    },
    onSwipeAreaSwipe(event) {
      if (event.direction === 4 && !this.$vs.rtl) {
        // Swipe Right
        if (!this.isVerticalNavMenuActive && this.showCloseButton)
          this.isVerticalNavMenuActive = true;
      } else if (event.direction === 2 && this.$vs.rtl) {
        // Swipe Left
        if (!this.isVerticalNavMenuActive && this.showCloseButton)
          this.isVerticalNavMenuActive = true;
      }
    },
    psSectionScroll() {
      const scroll_el = this.$refs.verticalNavMenuPs.$el || this.$refs.verticalNavMenuPs;
      this.showShadowBottom = scroll_el.scrollTop > 0;
    },
    mouseEnter() {
      if (this.reduce) this.$store.commit('UPDATE_VERTICAL_NAV_MENU_ITEMS_MIN', false);
      this.isMouseEnter = true;
    },
    mouseLeave() {
      if (this.reduce) this.$store.commit('UPDATE_VERTICAL_NAV_MENU_ITEMS_MIN', true);
      this.isMouseEnter = false;
    },
    setVerticalNavMenuWidth() {
      if (this.windowWidth > 1200) {
        if (this.layoutType === 'vertical') {
          // Set reduce
          this.reduce = !!this.reduceButton;

          // Open NavMenu
          this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', true);

          // Set Menu Items Only Icon Mode
          const verticalNavMenuItemsMin = !!(this.reduceButton && !this.isMouseEnter);
          this.$store.commit('UPDATE_VERTICAL_NAV_MENU_ITEMS_MIN', verticalNavMenuItemsMin);

          // Menu Action buttons
          this.clickNotClose = true;
          this.showCloseButton = false;

          const verticalNavMenuWidth = this.isVerticalNavMenuReduced ? 'reduced' : 'default';
          this.$store.dispatch('updateVerticalNavMenuWidth', verticalNavMenuWidth);

          return;
        }
      }

      // Close NavMenu
      this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', false);

      // Reduce button
      if (this.reduceButton) this.reduce = false;

      // Menu Action buttons
      this.showCloseButton = true;
      this.clickNotClose = false;

      // Update NavMenu Width
      this.$store.dispatch('updateVerticalNavMenuWidth', 'no-nav-menu');

      // Remove Only Icon in Menu
      this.$store.commit('UPDATE_VERTICAL_NAV_MENU_ITEMS_MIN', false);

      // if(this.layoutType === 'vertical' || (this.layoutType === 'horizontal' && this.windowWidth < 1200))
      // if (this.windowWidth < 1200) {

      //   // Close NavMenu
      //   this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', false)

      //   // Reduce button
      //   if (this.reduceButton) this.reduce = false

      //   // Menu Action buttons
      //   this.showCloseButton = true
      //   this.clickNotClose   = false

      //   // Update NavMenu Width
      //   this.$store.dispatch('updateVerticalNavMenuWidth', 'no-nav-menu')

      //   // Remove Only Icon in Menu
      //   this.$store.commit('UPDATE_VERTICAL_NAV_MENU_ITEMS_MIN', false)

      // } else {

      //   // Set reduce
      //   this.reduce = this.reduceButton ? true : false

      //   // Open NavMenu
      //   this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', true)

      //   // Set Menu Items Only Icon Mode
      //   const verticalNavMenuItemsMin = (this.reduceButton && !this.isMouseEnter) ? true : false
      //   this.$store.commit('UPDATE_VERTICAL_NAV_MENU_ITEMS_MIN', verticalNavMenuItemsMin)

      //   // Menu Action buttons
      //   this.clickNotClose   = true
      //   this.showCloseButton = false

      //   const verticalNavMenuWidth   = this.isVerticalNavMenuReduced ? "reduced" : "default"
      //   this.$store.dispatch('updateVerticalNavMenuWidth', verticalNavMenuWidth)
      // }
    },
    toggleReduce(val) {
      this.reduceButton = val;
      this.setVerticalNavMenuWidth();
    },
    logoutStudent() {
      this.$store.dispatch('authStudent/logout');
    },
  },
  mounted() {
    this.setVerticalNavMenuWidth();
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/vuexy/components/verticalNavMenu.scss';
</style>

<style lang="scss" scoped>
.menu-reduce {
  box-shadow: 0px 0px 5px 1px rgba(0, 0, 0, 0.2);
  background: #fff;
  height: 25px;
  width: 25px;
  border-radius: 15px;
  transition: all 0.2s;
  position: absolute;
  right: -10px;
  top: 50%;
  transform: translateY(-50%) scale(0.9);
  justify-content: center;
  align-items: center;

  &.active {
    background: #449f77;

    svg {
      color: #000;
    }
  }

  &:hover {
    background: #a2d7bf;
    box-shadow: 0px 0px 5px 1px rgba(0, 0, 0, 0);
    transform: translateY(-50%) scale(1.2);

    ::v-deep {
      svg {
        color: #000;
      }
    }
  }

  ::v-deep {
    svg {
      stroke-width: 3;
      height: 18px;
      width: 18px;
      color: #9f9f9f;
      font-size: 14px;
    }
  }
}

::v-deep {
  .vs-sidebar .vs-sidebar--items {
    overflow: scroll;
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }

  .vs-sidebar--items {
    overflow: initial;
  }
}
</style>
